import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import s from './Sidebar.module.scss'
import { LOGO } from '../../assets'

export interface NavEntry {
  to: string
  label: string
  /**
   * Was auf dem schmalen Streifen an der Stelle der Beschriftung steht. Ohne
   * Angabe der erste Buchstabe — dann bleibt der Streifen benutzbar, auch
   * bevor es Zeichen gibt.
   */
  icon?: string
  /** Zahl rechts im Eintrag, etwa die Anzahl Datensätze. */
  count?: number | string
}

export interface NavSection {
  /**
   * Überschrift der Gruppe, im Stil von POC v1: `// DATEN`.
   *
   * Optional: Eine Gruppe ohne Überschrift steht für sich. Gedacht für den
   * einen Eintrag, der kein Teil einer Ordnung ist, sondern der Einstieg.
   */
  label?: string
  /** Eindeutig, auch ohne Überschrift — React braucht einen Schlüssel. */
  key: string
  entries: NavEntry[]
}

interface Props {
  sections: NavSection[]
  user?: string
  onLogout?: () => void
  open: boolean
  onClose: () => void
  /** Zusätzliche Angaben über dem Benutzerbereich. */
  footer?: ReactNode
}

/**
 * Die Navigation.
 *
 * Die Einträge kommen von außen und stehen nicht hier: Was die Anwendung
 * anbietet, ist ihre Sache — diese Komponente weiß nur, wie eine Navigation
 * aussieht.
 */
export function Sidebar({ sections, user, onLogout, open, onClose, footer }: Props) {
  const location = useLocation()

  // Nach einem Sprung schließen. Sonst bleibt die Überlagerung über der Seite
  // stehen, die man gerade aufgerufen hat.
  useEffect(() => { onClose() }, [location.pathname])   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <nav className={s.nav} data-open={open} aria-label="Hauptnavigation">
        <div className={s.brand}>
          <img src={LOGO} alt="" />
          <div className={s.brandText}>
            <span className={s.brandName}>Instrument Watcher</span>
            <span className={s.brandSub}>Marktdaten</span>
          </div>
          <button type="button" className={s.close} onClick={onClose}
                  aria-label="Menü schließen">×</button>
        </div>

        <div className={s.links}>
          {sections.map((section) => (
            <div key={section.key} className={s.section}>
              {section.label && (
                <>
                  <div className={s.group}>
                    <span className={s.groupLabel}>// {section.label}</span>
                  </div>
                  <div className={s.groupRule} />
                </>
              )}
              {section.entries.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === '/'}
                  title={entry.label}
                  className={({ isActive }) => `${s.link} ${isActive ? s.active : ''}`}
                >
                  <span className={s.icon} aria-hidden="true">
                    {entry.icon ?? entry.label.charAt(0)}
                  </span>
                  <span className={s.linkLabel}>{entry.label}</span>
                  {entry.count !== undefined && (
                    <span className={s.count}>{entry.count}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className={s.footer}>{footer}</div>

        <div className={s.user}>
          <span className={s.userText}>
            <span className={s.userLabel}>angemeldet</span>
            <span className={s.userName}>{user ?? '—'}</span>
          </span>
          {onLogout && (
            <button type="button" className={s.logout} onClick={onLogout}
                    title="abmelden">Logout</button>
          )}
        </div>
      </nav>

      <div className={s.spacer} aria-hidden="true" />
    </>
  )
}
